import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { apiFetch } from './utils/apiClient';
import { clearUnsavedRunScanResults } from './utils/runScanResultsSession';
import { clearPortfolioReturnSessionId, getPortfolioReturnSessionId } from './utils/portfolioReturnSession';

// Import page components
import Home from './pages/Home';
import Methodology from './pages/Methodology';
import InvestmentDisclaimer from './pages/InvestmentDisclaimer';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Support from './pages/Support';
import Pricing from './pages/Pricing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Checkout from './pages/Checkout';
import Dashboard from './pages/Dashboard';
import RunScan from './pages/RunScan';
import SavedScans from './pages/SavedScans';
import Alerts from './pages/Alerts';
import Account from './pages/Account';
import Billing from './pages/Billing';
import ScanDetail from './pages/ScanDetail';
import PortfolioDetail from './pages/PortfolioDetail';
import StockResearch from './pages/StockResearch';
import Portfolios from './pages/Portfolios';
import ValidationLab from './pages/ValidationLab';
import AdminDataQuality from './pages/AdminDataQuality';
import AdminClientAnalytics from './pages/AdminClientAnalytics';
import AdminAccountManagement from './pages/AdminAccountManagement';
import AcceptInvitation from './pages/AcceptInvitation';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail, { normalizeEmailVerificationHashRoute } from './pages/VerifyEmail';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import AuthenticatedAppLayout from './components/AuthenticatedAppLayout';


function App() {
  normalizeEmailVerificationHashRoute();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    apiFetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/me`)
      .then((response) => setIsAuthenticated(response.ok))
      .catch(() => setIsAuthenticated(false))
      .finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    if (!authChecked) {
      return;
    }

    if (!isAuthenticated) {
      clearUnsavedRunScanResults();
      clearPortfolioReturnSessionId();
      return;
    }

    getPortfolioReturnSessionId();
  }, [authChecked, isAuthenticated]);

  if (!authChecked) return null;
  
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/investment-disclaimer" element={<InvestmentDisclaimer />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/login" element={<Login setIsAuthenticated={setIsAuthenticated} isAuthenticated={isAuthenticated} />} />
          <Route path="/signup" element={<Signup setIsAuthenticated={setIsAuthenticated} isAuthenticated={isAuthenticated} />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          
          {/* Protected logged-in routes */}
          <Route element={<ProtectedRoute isAuthenticated={isAuthenticated}><AuthenticatedAppLayout setIsAuthenticated={setIsAuthenticated} /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/scan" element={<RunScan />} />
            <Route path="/portfolios" element={<Portfolios />} />
            <Route path="/portfolios/:portfolioId" element={<PortfolioDetail />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/reports" element={<Navigate to="/dashboard" replace />} />
            <Route path="/account" element={<Account setIsAuthenticated={setIsAuthenticated} />} />
            <Route path="/preferences" element={<Navigate to="/account" replace />} />
            <Route path="/validation-lab" element={<ValidationLab />} />
            <Route path="/methodology" element={<Methodology />} />
            <Route path="/scans" element={<SavedScans />} />
            <Route path="/scans/:scanId" element={<ScanDetail />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/billing/checkout" element={<Checkout />} />
            <Route path="/stock" element={<StockResearch />} />
            <Route path="/stock/:ticker" element={<StockResearch />} />
            <Route path="/admin/data-quality" element={<AdminProtectedRoute><AdminDataQuality /></AdminProtectedRoute>} />
            <Route path="/admin/client-analytics" element={<AdminClientAnalytics />} />
            <Route path="/admin/account-management" element={<AdminProtectedRoute><AdminAccountManagement setIsAuthenticated={setIsAuthenticated} /></AdminProtectedRoute>} />
          </Route>
        </Routes>
      </div>
    </Router>
  );
}

export default App;
